/**
 * DailyPreferencesModal — Popup affiché au cold start pour calibrer la session
 *
 * Style Outfit "Soir de Paris" : charbon chaud + dorures champagne.
 * Slide-up depuis le bas, peut être skippé (mais on garde le choix vide).
 *
 * Demande à l'user :
 *   - Style(s) du jour (multi-select)
 *   - Occasion ciblée (single-select)
 *   - Budget (single-select)
 *   - Catégories recherchées (multi-select)
 */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  View, Text, TouchableOpacity, ScrollView, StyleSheet, StatusBar, Platform, ActivityIndicator,
} from 'react-native';
import AppModal from './AppModal';
import PrefIcon from './PrefIcon';
import PartnerLogo from './PartnerLogo';
// v1.10.86 : useSafeAreaInsets pour padding manuel iOS dynamic island
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import {
  setSessionPrefs, getLastUsedPrefs, EMPTY_PREFS, setCachedPrefs,
} from '../services/DailyPreferencesService';

const GOLD      = '#D4AF72';
const GOLD_DARK = '#B8860B';

// ═══ Options ═══
// `genders` = liste des genres pour lesquels l'option apparaît :
//   - ['F','M','N'] (par défaut) : visible pour tous
//   - ['F'] : visible uniquement pour femmes
//   - ['M'] : visible uniquement pour hommes
// Quand `genders` est absent, c'est unisex (visible partout).

const STYLES_OPTIONS = [
  { value: 'chic',        labelKey: 'style_chic',     emoji: '✨' },
  { value: 'casual',      labelKey: 'style_casual',   emoji: '👕' },
  { value: 'romantique',  labelKey: 'style_romantic', emoji: '🌹', genders: ['F'] },
  { value: 'bohème',      labelKey: 'style_boho',     emoji: '🌾' },
  { value: 'sport',       labelKey: 'style_sport',    emoji: '🏃' },
  { value: 'streetwear',  labelKey: 'style_streetwear', emoji: '🧢' },
  { value: 'minimaliste', labelKey: 'style_minimal',  emoji: '⚪' },
  { value: 'workwear',    labelKey: 'style_office',   emoji: '💼' },
];

const OCCASIONS = [
  { value: 'bureau',    labelKey: 'occ_work',     emoji: '💼' },
  { value: 'soirée',    labelKey: 'occ_evening',  emoji: '🥂' },
  { value: 'weekend',   labelKey: 'occ_weekend',  emoji: '☕' },
  { value: 'date',      labelKey: 'occ_date',     emoji: '💕' },
  { value: 'sport',     labelKey: 'occ_sport',    emoji: '🏋️' },
  { value: 'vacances',  labelKey: 'occ_vacation', emoji: '🏖️' },
  { value: 'cérémonie', labelKey: 'occ_ceremony', emoji: '👰' },
  { value: 'quotidien', labelKey: 'occ_daily',    emoji: '🚶' },
];

const BUDGETS = [
  { value: 'budget',    labelKey: 'budget_under_50',  subKey: 'budget_per_piece' },
  { value: 'mid-range', labelKey: 'budget_under_150', subKey: 'budget_per_piece' },
  { value: 'premium',   labelKey: 'budget_under_500', subKey: 'budget_per_piece' },
  { value: 'luxury',    labelKey: 'budget_over_500',  subKey: 'budget_per_piece' },
  { value: null,        labelKey: 'budget_no_limit',  subKey: 'budget_unlimited' },
];

// v1.12.296 — Choix des partenaires (filtre les sources catalogue)
// v1.12.366 — iqueens retiré (produits disabled en DB)
// v1.12.855 — drestige ajouté pour les hommes (luxe multi-marques Awin)
const PARTNERS = [
  { value: 'tous',                labelKey: 'partner_tous',     emoji: '🐻', genders: ['F', 'N'] }, // TOUS = F uniquement
  { value: 'suit_negozi',         labelKey: 'partner_suit',     emoji: '🤵' },
  // v1.12.338 — ANN + COUTR ajoutés
  { value: 'ann_demeulemeester',  labelKey: 'partner_ann',      emoji: '◾' },
  { value: 'coutr',               labelKey: 'partner_coutr',    emoji: '🇺🇸' },
  // v1.12.855 — DRESTIGE : multi-marques luxe homme + femme (Awin 13% commission)
  { value: 'drestige',            labelKey: 'partner_drestige', emoji: '👔' },
  // v1.12.877 — TAMARIS : chaussures + accessoires F (Tradedoubler 6%)
  { value: 'tamaris',             labelKey: 'partner_tamaris',  emoji: '👠', genders: ['F', 'N'] },
  // v1.12.944 — DESIGUAL : prêt-à-porter Homme + Femme (Tradedoubler 7,70%)
  { value: 'desigual',            labelKey: 'partner_desigual', emoji: '👗', genders: ['F', 'M', 'N'] },
];

const CATEGORIES = [
  { value: 'Hauts',       labelKey: 'cat_tops',        emoji: '👕' },
  { value: 'Bas',         labelKey: 'cat_bottoms',     emoji: '👖' },
  { value: 'Robes',       labelKey: 'cat_dresses',     emoji: '👗', genders: ['F'] },
  { value: 'Vestes',      labelKey: 'cat_jackets',     emoji: '🧥' },
  { value: 'Manteaux',    labelKey: 'cat_coats',       emoji: '🧥' }, // v1.12.204
  { value: 'Chaussures',  labelKey: 'cat_shoes',       emoji: '👠' },
  { value: 'Sacs',        labelKey: 'cat_bags',        emoji: '👜' },
  { value: 'Bijoux',      labelKey: 'cat_jewelry',     emoji: '💍', genders: ['F'] },
  { value: 'Accessoires', labelKey: 'cat_accessories', emoji: '🎀' },
];

/**
 * Filtre une liste d'options selon le genre user.
 * Une option sans `genders` est visible pour tous.
 * Une option avec `genders: ['F']` n'est visible que pour user.gender === 'F'.
 * Pour le genre 'N' (Autre), on affiche tout (l'user décide).
 */
function filterByGender(options, gender) {
  if (!gender || gender === 'N') return options; // 'Autre' → tout afficher
  return options.filter(opt => {
    if (!opt.genders) return true; // unisex
    return opt.genders.includes(gender);
  });
}

const getColors = (dark) => ({
  bg:        dark ? '#0E0C0A' : '#FAFAFA',
  surface:   dark ? '#1A1814' : '#FFFFFF',
  text:      dark ? '#EEEEEE' : '#1A1814',
  textMuted: dark ? '#A89E94' : '#7A7268',
  textFaint: dark ? '#666666' : '#A89E94',
  border:    dark ? '#2A2622' : '#EAE6E0',
  inputBg:   dark ? '#221F1A' : '#FFFFFF',
  goldLight: dark ? '#332A20' : '#FFF7E0',
  gold:      GOLD,
  goldDark:  dark ? '#D4B896' : GOLD_DARK,
});

export default function DailyPreferencesModal({ visible, onClose, onSubmit }) {
  const { dark, theme } = useTheme();
  const { user } = useAuth();
  const { t } = useI18n();
  const COLORS = getColors(dark);
  const styles = useMemo(() => makeStyles(COLORS), [dark]);
  // v1.10.86 : safe area insets
  const insets = useSafeAreaInsets();

  // ═══ Filtrage des options selon le genre user ═══
  // Pour un homme, on retire les options strictement féminines (Robes, Bijoux,
  // style Romantique). Pour 'N' (Autre) ou genre absent, on affiche tout.
  const userGender = user?.gender || null;
  const stylesOptions = useMemo(() => filterByGender(STYLES_OPTIONS, userGender), [userGender]);
  const occasions = useMemo(() => filterByGender(OCCASIONS, userGender), [userGender]);
  const categoryOptions = useMemo(() => filterByGender(CATEGORIES, userGender), [userGender]);
  // v1.12.296 — Partenaires : TOUS masque pour les hommes (0 article M)
  const partnersOptions = useMemo(() => filterByGender(PARTNERS, userGender), [userGender]);

  const [styles_, setStyles] = useState([]);
  const [occasion, setOccasion] = useState(null);
  const [budget, setBudget] = useState(undefined);     // undefined = pas choisi, null = illimité
  const [categories, setCategories] = useState([]);
  const [partners, setPartners] = useState([]); // v1.12.296
  const [submitting, setSubmitting] = useState(false);

  // Pré-remplir avec les dernières préférences utilisées (pour faciliter)
  // En filtrant pour ne garder que les options compatibles avec le genre actuel.
  useEffect(() => {
    if (!visible) return;
    (async () => {
      const last = await getLastUsedPrefs();
      if (last) {
        // Garder uniquement les valeurs qui sont dans la liste filtrée
        const validStyles = (last.styles || []).filter(s =>
          stylesOptions.some(o => o.value === s)
        );
        const validOccasion = last.occasion && occasions.some(o => o.value === last.occasion)
          ? last.occasion : null;
        const validCategories = (last.categories || []).filter(c =>
          categoryOptions.some(o => o.value === c)
        );
        setStyles(validStyles);
        setOccasion(validOccasion);
        setBudget(last.budget); // budget pas concerné par le genre
        setCategories(validCategories);
      } else {
        setStyles([]);
        setOccasion(null);
        setBudget(undefined);
        setCategories([]);
      }
    })();
  }, [visible, stylesOptions, occasions, categoryOptions]);

  // v1.12.227 — Limite 2 sélections max sur Style et Catégories.
  // Si l'user en a déjà 2 et clique sur une 3ème → on remplace la plus ancienne (FIFO).
  const MAX_MULTI = 2;
  const toggleStyle = (v) => {
    setStyles(curr => {
      if (curr.includes(v)) return curr.filter(x => x !== v);
      if (curr.length >= MAX_MULTI) return [...curr.slice(1), v];
      return [...curr, v];
    });
  };
  const toggleCategory = (v) => {
    setCategories(curr => {
      if (curr.includes(v)) return curr.filter(x => x !== v);
      if (curr.length >= MAX_MULTI) return [...curr.slice(1), v];
      return [...curr, v];
    });
  };

  const handleSubmit = async () => {
    try { require('../services/AnalyticsService').trackTap('ui_prefs_submit', { styles: styles_.length, has_occasion: !!occasion, categories: categories.length }); } catch {}
    setSubmitting(true);
    const prefs = {
      ...EMPTY_PREFS,
      styles: styles_,
      occasion,
      // v1.13.34 — Filtres budget + partenaires retirés des préférences → jamais
      // appliqués (null/[]), même si un ancien choix avait été enregistré.
      budget: null,
      categories,
      partners: [],
    };
    try {
      await setSessionPrefs(prefs);
      setCachedPrefs(prefs);
      if (onSubmit) onSubmit(prefs);
    } catch (e) {
      console.warn('[DailyPrefsModal] save failed:', e.message);
    } finally {
      setSubmitting(false);
      if (onClose) onClose();
    }
  };

  // v1.12.626 — handleSkip stabilisé via useCallback + refs pour les callbacks
  // parent. Sans ça, sa ref change à chaque render → AppModal useEffect cleanup
  // → window.history.back() → popstate → handleSkip fire → submit EMPTY_PREFS
  // → close. Symptôme : modal s'ouvre et se ferme immédiatement, prefs reset.
  const onCloseRef = useRef(onClose);
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { onSubmitRef.current = onSubmit; }, [onSubmit]);
  // v1.13.18 — GA : ouverture du modal préférences
  useEffect(() => { if (visible) { try { require('../services/AnalyticsService').trackTap('ui_prefs_open'); } catch {} } }, [visible]);
  const handleSkip = useCallback(async () => {
    try { require('../services/AnalyticsService').trackTap('ui_prefs_skip'); } catch {}
    setSubmitting(true);
    try {
      await setSessionPrefs(EMPTY_PREFS);
      setCachedPrefs(EMPTY_PREFS);
      if (onSubmitRef.current) onSubmitRef.current(EMPTY_PREFS);
    } catch {}
    finally {
      setSubmitting(false);
      if (onCloseRef.current) onCloseRef.current();
    }
  }, []);

  return (
    <AppModal
      visible={visible}
      animationType="slide"
      transparent={false}
      onRequestClose={handleSkip}
      presentationStyle="overFullScreen"
      statusBarTranslucent
    >
      <View style={[styles.safe, { paddingTop: Math.max(insets.top, 20) }]}>
        <StatusBar barStyle={dark ? 'light-content' : 'dark-content'} />
        <View style={styles.header}>
          <View style={{ flex: 1 }} />
          <TouchableOpacity onPress={handleSkip} style={styles.skipBtn}>
            <Text style={styles.skipText}>{t('skip')}</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          {/* Hero */}
          <View style={styles.hero}>
            <Text style={styles.heroBadge}>{t('daily_prefs_today_badge')}</Text>
            <Text style={styles.heroTitle}>{t('daily_prefs_hero_title')}</Text>
            <Text style={styles.heroSub}>
              {t('daily_prefs_hero_sub')}
            </Text>
          </View>

          {/* Style */}
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>{t('daily_prefs_q_style')} <Text style={styles.optional}>{t('daily_prefs_max_2')}</Text></Text>
            <View style={styles.chipsRow}>
              {stylesOptions.map(opt => {
                const active = styles_.includes(opt.value);
                return (
                  <TouchableOpacity
                    key={opt.value}
                    onPress={() => toggleStyle(opt.value)}
                    style={[styles.chip, active && styles.chipActive]}
                    activeOpacity={0.85}
                  >
                    <View style={styles.chipIcon}>
                      <PrefIcon kind="style" value={opt.value} gender={userGender} size={20} color={active ? GOLD_DARK : COLORS.text} accent={GOLD} />
                    </View>
                    <Text style={[styles.chipText, active && styles.chipTextActive]}>{t(opt.labelKey)}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* Occasion */}
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>{t('daily_prefs_q_occasion')}</Text>
            <View style={styles.chipsRow}>
              {occasions.map(opt => {
                const active = occasion === opt.value;
                return (
                  <TouchableOpacity
                    key={opt.value}
                    onPress={() => setOccasion(active ? null : opt.value)}
                    style={[styles.chip, active && styles.chipActive]}
                    activeOpacity={0.85}
                  >
                    <View style={styles.chipIcon}>
                      <PrefIcon kind="occasion" value={opt.value} gender={userGender} size={20} color={active ? GOLD_DARK : COLORS.text} accent={GOLD} />
                    </View>
                    <Text style={[styles.chipText, active && styles.chipTextActive]}>{t(opt.labelKey)}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* v1.13.34 — Filtre Budget retiré des préférences (sur demande) */}

          {/* Catégories */}
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>{t('daily_prefs_q_categories')} <Text style={styles.optional}>{t('daily_prefs_max_2')}</Text></Text>
            <View style={styles.chipsRow}>
              {categoryOptions.map(opt => {
                const active = categories.includes(opt.value);
                return (
                  <TouchableOpacity
                    key={opt.value}
                    onPress={() => toggleCategory(opt.value)}
                    style={[styles.chip, active && styles.chipActive]}
                    activeOpacity={0.85}
                  >
                    <View style={styles.chipIcon}>
                      <PrefIcon kind="category" value={opt.value} gender={userGender} size={20} color={active ? GOLD_DARK : COLORS.text} accent={GOLD} />
                    </View>
                    <Text style={[styles.chipText, active && styles.chipTextActive]}>{t(opt.labelKey)}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {/* v1.13.34 — Filtre Partenaires retiré des préférences (sur demande) */}

          {/* CTA */}
          <TouchableOpacity
            style={[styles.cta, submitting && styles.ctaDisabled]}
            onPress={handleSubmit}
            disabled={submitting}
            activeOpacity={0.88}
          >
            {submitting ? (
              <ActivityIndicator color="#0E0C0A" />
            ) : (
              <Text style={styles.ctaText}>{t('daily_prefs_cta')}</Text>
            )}
          </TouchableOpacity>

          <Text style={styles.footer}>
            {t('daily_prefs_footer')}
          </Text>
        </ScrollView>
      </View>
    </AppModal>
  );
}

const makeStyles = (C) => StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: C.bg,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 12,
    // ═══ FIX 2026-04 : éviter que "Ignorer" colle à la status bar Android ═══
    // Avec statusBarTranslucent={true} sur le Modal, le contenu s'étend sous
    // la status bar. Sur iOS le SafeAreaView gère ça via l'encoche, mais sur
    // Android il faut ajouter manuellement la hauteur de la status bar.
    // StatusBar.currentHeight n'existe que sur Android (undefined sur iOS).
    paddingTop: Platform.OS === 'android'
      ? (StatusBar.currentHeight || 24) + 12
      : 12,
  },
  skipBtn: {
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  skipText: {
    fontSize: 13,
    color: C.textMuted,
    fontWeight: '500',
  },
  content: {
    padding: 20,
    paddingBottom: 40,
  },
  hero: {
    marginBottom: 28,
    paddingHorizontal: 4,
  },
  heroBadge: {
    fontSize: 9,
    letterSpacing: 3,
    color: C.goldDark,
    fontWeight: '700',
    marginBottom: 8,
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: '300',
    color: C.text,
    letterSpacing: 0.3,
    marginBottom: 8,
    lineHeight: 34,
  },
  heroSub: {
    fontSize: 13,
    color: C.textMuted,
    lineHeight: 19,
  },
  section: {
    marginBottom: 24,
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: 1.5,
    color: C.textMuted,
    fontWeight: '700',
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  // ═══ v1.9.36 : sous-titre explicatif sous certains labels ═══
  sectionHint: {
    fontSize: 11,
    color: C.textFaint,
    marginTop: -8,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  optional: {
    color: C.textFaint,
    fontWeight: '400',
    letterSpacing: 0,
    textTransform: 'none',
    fontSize: 10,
  },
  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: C.border,
    backgroundColor: C.inputBg,
  },
  chipActive: {
    borderColor: C.gold,
    backgroundColor: C.goldLight,
  },
  chipEmoji: {
    fontSize: 14,
  },
  chipIcon: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipText: {
    fontSize: 13,
    color: C.textMuted,
    fontWeight: '500',
  },
  chipTextActive: {
    color: C.goldDark,
    fontWeight: '700',
  },
  budgetRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  budgetCard: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: C.border,
    backgroundColor: C.inputBg,
    minWidth: 110,
  },
  budgetCardActive: {
    borderColor: C.gold,
    backgroundColor: C.goldLight,
  },
  budgetLabel: {
    fontSize: 14,
    color: C.text,
    fontWeight: '600',
  },
  budgetLabelActive: {
    color: C.goldDark,
  },
  budgetSub: {
    fontSize: 10,
    color: C.textFaint,
    marginTop: 2,
  },
  budgetSubActive: {
    color: C.goldDark,
  },
  cta: {
    marginTop: 12,
    backgroundColor: C.gold,
    paddingVertical: 16,
    borderRadius: 100,
    alignItems: 'center',
  },
  ctaDisabled: {
    opacity: 0.5,
  },
  ctaText: {
    color: '#0E0C0A',
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 1.5,
  },
  footer: {
    marginTop: 16,
    fontSize: 11,
    color: C.textFaint,
    textAlign: 'center',
    lineHeight: 16,
  },
});
